import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { financeiroApi } from '../../../services/api';
import type { ContaBancaria, TipoConta } from '../../../types';

interface ContaBancariaFormProps {
  mode: 'create' | 'edit';
}

const ContaBancariaForm: React.FC<ContaBancariaFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    nome: '',
    banco: '',
    tipo: 'CC' as TipoConta,
    agencia: '',
    conta: '',
    saldo_inicial: '',
    ativa: true,
    data_abertura: '',
    observacoes: '',
  });

  useEffect(() => {
    if (isEdit && id) {
      loadConta(id);
    }
  }, [isEdit, id]);

  const loadConta = async (contaId: string) => {
    try {
      setLoading(true);
      const conta = await financeiroApi.getConta(Number(contaId));
      setFormData({
        nome: conta.nome || '',
        banco: conta.banco || '',
        tipo: conta.tipo || 'CC',
        agencia: conta.agencia || '',
        conta: conta.conta || '',
        saldo_inicial: conta.saldo_inicial?.toString() || '0',
        ativa: conta.ativa !== undefined ? conta.ativa : true,
        data_abertura: conta.data_abertura || '',
        observacoes: conta.observacoes || '',
      });
    } catch (error) {
      console.error('Error loading conta:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Formata valor monetário: últimos 2 dígitos são sempre decimais (formato BR: vírgula)
  const formatarValorMonetario = (valor: string): string => {
    const apenasNumeros = valor.replace(/\D/g, '');
    if (!apenasNumeros) return '';
    const valorNumerico = parseInt(apenasNumeros, 10) / 100;
    return valorNumerico.toFixed(2).replace('.', ',');
  };

  // Converte valor BR (vírgula) para formato numérico (ponto) para envio ao backend
  const valorParaNumero = (valor: string): number => {
    if (!valor) return 0;
    return parseFloat(valor.replace(',', '.')) || 0;
  };

  const handleSaldoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorFormatado = formatarValorMonetario(e.target.value);
    setFormData(prev => ({ ...prev, saldo_inicial: valorFormatado }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.banco.trim()) {
      newErrors.banco = 'Banco é obrigatório';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        nome: formData.nome,
        banco: formData.banco,
        tipo: formData.tipo,
        agencia: formData.agencia || undefined,
        conta: formData.conta || undefined,
        saldo_inicial: valorParaNumero(formData.saldo_inicial),
        ativa: formData.ativa,
        data_abertura: formData.data_abertura || undefined,
        observacoes: formData.observacoes || undefined,
      };

      if (isEdit && id) {
        await financeiroApi.updateConta(Number(id), payload);
      } else {
        await financeiroApi.createConta(payload);
      }
      navigate('/financeiro/contas');
    } catch (error: any) {
      console.error('Error saving conta:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-1">
        <h1 className="text-base font-bold text-secondary-900">
          {isEdit ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
        </h1>
        <p className="text-xs text-secondary-600">
          {isEdit ? 'Atualize as informações da conta' : 'Cadastre uma nova conta bancária'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-1">
        {/* Informações Básicas */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Informações Básicas</h3>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-1">
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Nome da Conta *
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                className={`input-field ${errors.nome ? 'border-red-300' : ''}`}
                placeholder="Ex: Conta Corrente Principal"
                required
              />
              {errors.nome && <p className="text-red-600 text-xs mt-0.5">{errors.nome}</p>}
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Banco *
              </label>
              <input
                type="text"
                name="banco"
                value={formData.banco}
                onChange={handleChange}
                className={`input-field ${errors.banco ? 'border-red-300' : ''}`}
                placeholder="Ex: Banco do Brasil"
                required
              />
              {errors.banco && <p className="text-red-600 text-xs mt-0.5">{errors.banco}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Tipo *
              </label>
              <select
                name="tipo"
                value={formData.tipo}
                onChange={handleChange}
                className={`input-field ${errors.tipo ? 'border-red-300' : ''}`}
                required
              >
                <option value="CC">Conta Corrente</option>
                <option value="CP">Conta Poupança</option>
                <option value="CI">Conta Investimento</option>
                <option value="DI">Dinheiro</option>
              </select>
              {errors.tipo && <p className="text-red-600 text-xs mt-0.5">{errors.tipo}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Agência
              </label>
              <input
                type="text"
                name="agencia"
                value={formData.agencia}
                onChange={handleChange}
                className="input-field"
                placeholder="0001"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Conta
              </label>
              <input
                type="text"
                name="conta"
                value={formData.conta}
                onChange={handleChange}
                className="input-field"
                placeholder="12345-6"
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Saldo Inicial
              </label>
              <input
                type="text"
                inputMode="numeric"
                name="saldo_inicial"
                value={formData.saldo_inicial}
                onChange={handleSaldoChange}
                className="input-field"
                placeholder="0,00"
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Data de Abertura
              </label>
              <input
                type="date"
                name="data_abertura"
                value={formData.data_abertura}
                onChange={handleChange}
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Status</h3>
          <div className="flex items-center">
            <input
              id="ativa"
              name="ativa"
              type="checkbox"
              checked={formData.ativa}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
            />
            <label htmlFor="ativa" className="ml-2 block text-xs text-secondary-900">
              Conta ativa
            </label>
          </div>
        </div>

        {/* Observações */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Observações</h3>
          <div>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleChange}
              rows={2}
              className="input-field"
              placeholder="Observações adicionais sobre a conta..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-1 mt-2">
          <button
            type="button"
            onClick={() => navigate('/financeiro/contas')}
            className="btn-secondary"
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Salvando...
              </div>
            ) : (
              isEdit ? 'Atualizar Conta' : 'Criar Conta'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContaBancariaForm;
